import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DownloadSimple, DeviceMobile, Share, CheckCircle } from "@phosphor-icons/react";

/**
 * Always-accessible "Install desktop app" trigger.
 *
 * The auto banner (`PWAInstallBanner`) only shows once and is gated by a
 * 14-day dismissal cookie plus the browser's own install-prompt criteria.
 * Partners who miss it or dismissed it should always be able to install from
 * Settings — that's what this button is for.
 *
 * Behaviour cascade:
 *   1. If Chrome/Edge held a `beforeinstallprompt` event → fire it directly.
 *   2. If iOS Safari (which never fires the event) → open a platform-specific
 *      instructions dialog inline.
 *   3. Otherwise → show a hint pointing at the browser's URL-bar install icon.
 *
 * Also clears the auto-banner dismissal flag so the passive banner can appear
 * again on the next visit if the install didn't complete.
 */
const DISMISSED_KEY = "yadid_pwa_dismissed_at";

export default function PWAInstallButton({ compact = false }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const platformHint = () => {
    const ua = window.navigator.userAgent || "";
    const isIOS = /iP(hone|od|ad)/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    const isFirefox = /firefox/i.test(ua);
    if (isIOS && isSafari) {
      return "On iOS Safari: tap the Share button (square with an up-arrow) at the bottom of the browser, then choose 'Add to Home Screen'.";
    }
    if (isFirefox) {
      return "Firefox doesn't install PWAs on desktop. Open YadidOS in Chrome, Edge, or Arc to install.";
    }
    return "Look for the install icon (a small computer-with-arrow icon) at the right end of your browser's address bar and click it. If you don't see it, refresh the page — Chrome/Edge sometimes take a moment to detect installability.";
  };

  const install = async () => {
    // Reset the auto-banner dismissal so the partner can be re-prompted later
    // if the install doesn't complete this round.
    localStorage.removeItem(DISMISSED_KEY);

    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      setDeferred(null);
      if (choice?.outcome === "accepted") {
        setInstalled(true);
        toast.success("YadidOS is installing to your desktop");
      } else {
        toast.info("Install cancelled — you can retry any time from Settings");
      }
      return;
    }
    // No deferred prompt available — show platform-specific instructions.
    setShowTip(true);
  };

  if (installed) {
    // Nothing useful to render once the partner has already installed. In
    // compact mode we render a tiny inline badge (used by callers that want a
    // status pip); in the full Settings panel we return null so the grid
    // doesn't sprout a floating "Installed" chip next to the canary.
    if (!compact) return null;
    return (
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-emerald-500 flex items-center gap-1" data-testid="pwa-installed-badge">
        <CheckCircle size={11} weight="fill" /> Installed
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={install}
        data-testid="pwa-install-sidebar-btn"
        className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-eyebrow inline-flex items-center gap-1"
      >
        <DownloadSimple size={11} weight="bold" /> Install app
      </button>
    );
  }

  return (
    <div className="border border-border bg-card p-6 md:col-span-2" data-testid="pwa-install-panel">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 border border-primary/60 bg-primary/10 flex items-center justify-center shrink-0">
          <DeviceMobile size={20} weight="fill" className="text-primary" />
        </div>
        <div className="flex-1">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow flex items-center gap-1.5">
            <DownloadSimple size={12} weight="fill" /> Install as desktop app
          </div>
          <div className="font-display font-bold text-base mt-1">Drop YadidOS onto your dock or Start menu.</div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Runs in its own window — no browser tabs, no address bar. Loads straight to your Command Center. Same login, same data, same cookie session.
          </p>

          {showTip && (
            <div className="mt-4 border-l-2 border-primary/60 bg-primary/5 p-3 text-xs text-muted-foreground leading-relaxed" data-testid="pwa-install-tip">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-1 flex items-center gap-1.5">
                <Share size={11} weight="bold" /> Install instructions
              </div>
              {platformHint()}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              onClick={install}
              data-testid="pwa-install-settings-btn"
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <DownloadSimple size={14} weight="bold" className="mr-2" />
              {deferred ? "Install now" : "Show install instructions"}
            </Button>
            {!deferred && (
              <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground self-center">
                Best in Chrome, Edge, Arc, or Brave
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
